"use client";

import AccreditationCard2, {
  type AccreditationCard2Item,
} from "@/components/molecules/AccreditationCard2";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { useMemo, useRef } from "react";

interface MetaItem extends AccreditationCard2Item {
  meta_priority: number;
  meta_status?: boolean;
}

interface Accreditation5Data {
  id: number;
  page_id?: number;
  status?: boolean;
  background_color?: string;
  title_part_1?: string;
  title_part_2?: string;
  title_part_3?: string;
  title_part_4?: string;
  title_primary_color?: string;
  title_accent_color?: string;
  metadata?: MetaItem[];
}

interface Accreditation5Props {
  /** Flat row from API, or `{ data: row }` from some admin previews */
  data: Accreditation5Data | { data: Accreditation5Data };
}

function resolveAccreditationPayload(
  data: Accreditation5Props["data"]
): Accreditation5Data | undefined {
  if (!data || typeof data !== "object") return undefined;
  const anyData = data as Record<string, unknown>;
  if ("data" in anyData && anyData.data != null && typeof anyData.data === "object") {
    return anyData.data as Accreditation5Data;
  }
  return data as Accreditation5Data;
}

const Accreditation5 = ({ data }: Accreditation5Props) => {
  const ref = useRef<HTMLElement>(null);
  const section = resolveAccreditationPayload(data);

  const {
    background_color = "#F8F8F8",
    title_part_1 = "",
    title_part_2 = "",
    title_part_3 = "",
    title_part_4 = "",
    title_primary_color = "#171719",
    title_accent_color = "#E1473F",
    metadata = [],
  } = section || {};

  const sortedMetadata = useMemo(() => {
    if (!metadata?.length) return [];
    return [...metadata]
      .filter((item) => item?.meta_status !== false)
      .sort((a, b) => a.meta_priority - b.meta_priority);
  }, [metadata]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "accreditation_section");
  //   }
  // }, []);

  const active = section?.status === true || section?.status === 1;
  if (!active) {
    return null;
  }

  if (sortedMetadata.length === 0) {
    return null;
  }

  const showHeadline =
    title_part_1 || title_part_2 || title_part_3 || title_part_4;

  return (
    <section
      ref={ref}
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "flex justify-center w-full items-center",
        "px-5 py-12 min-[768px]:px-16 min-[1024px]:px-[104px] min-[768px]:py-[72px]"
      )}
      style={{
        backgroundColor: background_color,
        fontFamily: "Poppins, sans-serif",
      }}
    >
      <div className="flex flex-col items-center gap-8 min-[768px]:gap-[42px] w-full max-w-[1200px]">
        {showHeadline && (
          <h2
            className={cn(
              "text-center font-semibold text-[28px] leading-tight",
              "min-[640px]:text-[30px] min-[768px]:text-[32px] max-w-[920px]",
              "inline-flex flex-wrap justify-center gap-x-2 gap-y-1"
            )}
          >
            {title_part_1 ? (
              <span style={{ color: title_primary_color }}>{title_part_1.trim()}</span>
            ) : null}
            {title_part_2 ? (
              <span style={{ color: title_accent_color }}>{title_part_2.trim()}</span>
            ) : null}
            {title_part_3 ? (
              <span style={{ color: title_primary_color }}>{title_part_3.trim()}</span>
            ) : null}
            {title_part_4 ? (
              <span style={{ color: title_accent_color }}>{title_part_4.trim()}</span>
            ) : null}
          </h2>
        )}

        <div
          className={cn(
            "grid w-full gap-5 justify-center",
            "grid-cols-[minmax(0,288px)]",
            "min-[640px]:grid-cols-[repeat(2,minmax(0,288px))]",
            "min-[1024px]:grid-cols-[repeat(3,minmax(0,288px))]"
          )}
        >
          {sortedMetadata.map((item, index) => (
            <AccreditationCard2
              key={`${item.id}-${index}`}
              item={item}
              className="h-full"
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Accreditation5;
